import React from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';
import { UnknownObject } from '../../../../../../../react-app-env';

import { Box, Button, Divider, Grid, TextField, Typography } from '@mui/material';

import { CheckCircleOutlined, ErrorOutlineOutlined } from '@mui/icons-material';

import PathwayReadOnlyView from '../../../../../../../components/PathwayBuilder/View/readonly';
import SpecialityCard from '../../../../../../../components/SpecialityCard';
import { Case, CasePot, CasePotStatusRequiresReason, CaseReducerAction, EnumPotStatus, validation } from '../../case';

import styles from './styles';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  caseData: Case | UnknownObject;
  potStatuses: EnumPotStatus[];
  validate: () => void;
  dispatch: React.Dispatch<{ type: CaseReducerAction; value: any }>;
}

const StepPots = ({ classes, caseData, potStatuses, validate, dispatch }: Props): React.ReactElement => {
  const getSelectedPot = () => caseData.pots.find((i: CasePot) => i.selected);

  const handleSomthingIsWrong = (pot: CasePot) => {
    dispatch({ type: CaseReducerAction.QUERY_POT, value: { id: pot.id } });
  };

  const handleCancelQuery = (pot: CasePot) => {
    dispatch({ type: CaseReducerAction.CANCEL_QUERY_POT, value: { id: pot.id } });
  };

  const handleCheckIn = (pot: CasePot) => {
    dispatch({ type: CaseReducerAction.CHECK_IN_POT, value: { id: pot.id } });
  };

  const getPotContent = () => {
    const pot = getSelectedPot();
    if (pot) {
      return (
        <>
          <Grid container ml={1}>
            <Grid item xs={6}>
              <Grid container rowSpacing={2}>
                <Grid item xs={12}>
                  <Typography className={classes.headerTitle} variant="h5">
                    {pot.name}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <PathwayReadOnlyView pathway={pot.pathway} />
                  {pot.note && (
                    <TextField multiline fullWidth disabled variant="outlined" label="Additional information" value={pot.note} rows={4} InputLabelProps={{ shrink: true }} />
                  )}
                </Grid>

                <Grid item xs={12}>
                  {!CasePotStatusRequiresReason.includes(pot.status) && (
                    <>
                      <Button variant="contained" color="primary" fullWidth onClick={() => handleCheckIn(pot)}>
                        Check-In
                      </Button>
                      <Button className={classes.reasonButton} variant="outlined" color="primary" fullWidth onClick={() => handleSomthingIsWrong(pot)}>
                        Something is wrong
                      </Button>
                    </>
                  )}

                  {CasePotStatusRequiresReason.includes(pot.status) && (
                    <div className={classes.query}>
                      <TextField
                        multiline
                        fullWidth
                        variant="outlined"
                        label="Query"
                        placeholder="Type query here"
                        value={pot.reason}
                        rows={8}
                        maxRows={12}
                        error={!pot.reason}
                        helperText={!pot.reason ? 'Please provide a query' : ''}
                        InputLabelProps={{ shrink: true }}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          dispatch({ type: CaseReducerAction.UPDATE_POT_REASON, value: { reason: event.target.value, id: pot.id } })
                        }
                      />
                      <Button className={classes.reasonButton} variant="outlined" fullWidth onClick={() => handleCancelQuery(pot)}>
                        Cancel
                      </Button>
                    </div>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      );
    }
    return <Typography variant="subtitle1">Sample not selected</Typography>;
  };

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h4">Sample Information</Typography>
        </div>
        <Grid container spacing={1}>
          <Grid display={'flex'} alignItems={'center'} item xs={4}>
            <Box height={'100%'}>
              {(caseData as Case).surgical_pathways.map((version) => {
                const pots = (caseData as Case).pots.filter((p) => p.speciality_category_id === version.category.id);
                return (
                  <Box mr={2}>
                    <Typography variant="overline">{version.category.name}</Typography>
                    <Box>
                      {pots.map((pot: CasePot) => (
                        <SpecialityCard
                          key={pot.id}
                          small
                          gridSize={12}
                          title={pot.name}
                          subTitle={potStatuses.find((i) => i.status === pot.status)?.description || undefined}
                          description={pot.pathway.map((pathway) => pathway.label).join(', ')}
                          statusIcon={validation.isPotValid(pot) ? <CheckCircleOutlined /> : <ErrorOutlineOutlined />}
                          statusIconColour={validation.isPotValid(pot) ? 'success' : 'error'}
                          selected={pot.selected}
                          onClick={() => dispatch({ type: CaseReducerAction.SELECT_POT, value: { id: pot.id } })}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Divider sx={{ height: '100%' }} orientation="vertical" />
          </Grid>
          <Grid item xs={8}>
            {getPotContent()}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default withStyles(styles)(StepPots);
